import { useState, useEffect, useCallback } from "react";
import { Grid, MenuItem, Select, FormControl, Button, TextField } from "@mui/material";
import styles from "./styles.module.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as PrinterIcon } from "images/svg/printer-grey.svg";
import { ReactComponent as FormatIcon } from "images/svg/document.svg";
import { INVOICE_PRINTER, PRINTER_TYPES, PRINTER_COMMANDS } from "const";
import toast from "react-hot-toast";
import AppBridgeHelper from "helpers/AppBridgeHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import { textToBytes, mergeBytes, bytesToBase64 } from "helpers/StringHelper";
import { PrinterManager } from "managers/PrinterManager";

const lineWidth = 48;

function InvoicePrintersPage() {
  const [printerType, setPrinterType] = useState("");
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [invoiceFormat, setInvoiceFormat] = useState("");

  const loadInstalledPrinters = useCallback(async () => {
    const bridge = AppBridgeHelper.getBridge();
    if (!bridge) return;

    try {
      if (printerType === PRINTER_TYPES.DIRECT_USB) {
        if (typeof bridge.getDirectUsbPrinters === "function" || bridge.getDirectUsbPrinters) {
          const devicesJson = await bridge.getDirectUsbPrinters();
          if (devicesJson) {
            const devicesList = JSON.parse(devicesJson);
            if (Array.isArray(devicesList)) {
              const formatted = devicesList.map((device, index) => ({
                id: device.Path || `direct-usb-${index}`,
                name: device.Name || "Direct USB Printer",
                path: device.Path,
              }));
              setPrinters(formatted);
              return;
            }
          }
        }
      } else {
        if (typeof bridge.getInstalledPrinters === "function" || bridge.getInstalledPrinters) {
          const printersJson = await bridge.getInstalledPrinters();
          if (printersJson) {
            const printersList = JSON.parse(printersJson);
            if (Array.isArray(printersList)) {
              const formatted = printersList.map((name, index) => ({
                id: `usb-${index}`,
                name: name,
              }));
              setPrinters(formatted);
              return;
            }
          }
        }
      }
    } catch (err) {
      console.error("Failed to load installed printers:", err);
    }
    setPrinters([]);
  }, [printerType]);

  useEffect(() => {
    const settings = TerminalHelper.getTerminalSettings();
    const savedFormat = settings.invoicePrinterFormat;
    const savedType = settings.printerType;
    const savedPrinter = settings.printer;
    const savedIP = settings.invoice_printer_ip;

    if (savedType) setPrinterType(savedType);
    if (savedPrinter) setSelectedPrinter(savedPrinter);
    if (savedFormat) setInvoiceFormat(savedFormat);
    if (savedIP) setIpAddress(savedIP);
  }, []);

  useEffect(() => {
    if (printerType === PRINTER_TYPES.USB || printerType === PRINTER_TYPES.DIRECT_USB) {
      loadInstalledPrinters();
    } else {
      setPrinters([]);
    }
  }, [printerType, loadInstalledPrinters]);

  const handleReloadPrinters = async () => {
    if (!printerType) return toast.error("Please select a printer type first");

    if (printerType === PRINTER_TYPES.USB || printerType === PRINTER_TYPES.DIRECT_USB) {
      await loadInstalledPrinters();
      toast.success("Printers reloaded");
    }
  };

  const handleSave = async () => {
    if (!invoiceFormat) return toast.error("Please select invoice format");

    try {
      const success = await TerminalHelper.updateTerminalSettings({
        invoicePrinterFormat: invoiceFormat,
        printerType,
        printer: selectedPrinter,
        invoice_printer_ip: ipAddress,
      });

      if (success) {
        toast.success("Printer Configuration Saved");
      }
    } catch (err) {
      toast.error("Failed to sync settings with server");
    }
  };

  const handleTestPrint = async () => {
    const bridge = AppBridgeHelper.getBridge();
    if (!bridge) {
      return toast.error("No printer bridge found");
    }

    try {
      const lines = [];
      lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
      lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
      lines.push(textToBytes("Sanoft POS\nPrinter Test\n"));
      lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
      lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
      lines.push(textToBytes("-".repeat(lineWidth)));
      lines.push(textToBytes("Printer configured for SANOFT POS!\n"));
      lines.push(textToBytes("-".repeat(lineWidth)));
      lines.push(PRINTER_COMMANDS.FEED_PAPER);
      lines.push(PRINTER_COMMANDS.CUT_CHARS);
      lines.push(PRINTER_COMMANDS.BEEP_SOUND);

      const content = bytesToBase64(mergeBytes(lines));
      const target = printerType === PRINTER_TYPES.LAN ? ipAddress : selectedPrinter || null;
      await PrinterManager.print({
        content,
        printerType: printerType || PRINTER_TYPES.USB,
        printerTarget: target,
        label: `INVOICE TEST`,
      });
    } catch (err) {
      console.error(err);
      toast.error("Test print failed");
    }
  };

  return (
    <Grid container className={styles.contentWrapper} mt={0} rowGap={2}>
      {/* Printer Type */}
      <Grid item xs={12} className={styles.titleWrapper}>
        <Grid className={styles.formSection}>
          <PrinterIcon />
          <h3 className={styles.subTitle}>Printer Type :</h3>
          <FormControl variant="standard" className={styles.selectWrapper}>
            <Select
              value={printerType}
              onChange={(e) => {
                setPrinterType(e.target.value);
                setPrinters([]);
                setSelectedPrinter("");
                setIpAddress("");
              }}
              displayEmpty
              disableUnderline
              IconComponent={ExpandMoreIcon}
              renderValue={(value) => {
                if (!value) return <span style={{ color: "#aaa" }}>Select Printer Type</span>;
                const displayValue = value === PRINTER_TYPES.DIRECT_USB ? "USB Without Driver" : value;
                return <span className={styles.mainTitle}>{displayValue}</span>;
              }}
              sx={{
                "& .MuiSelect-select": { padding: 0 },
                "& .MuiSelect-icon": { color: "#7a7a7a", right: "8px" },
              }}
            >
              {INVOICE_PRINTER.type.map((type, i) => (
                <MenuItem key={i} value={type}>
                  {type === PRINTER_TYPES.DIRECT_USB ? "USB Without Driver" : type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {[PRINTER_TYPES.USB, PRINTER_TYPES.DIRECT_USB].includes(printerType) && (
        <Grid item xs={12} className={styles.titleWrapper}>
          <Grid className={styles.formSection}>
            <PrinterIcon />
            <h3 className={styles.subTitle}>
              {printerType === PRINTER_TYPES.USB ? "USB Printer :" : "Direct USB Printer :"}
            </h3>
            <FormControl variant="standard" className={styles.selectWrapper}>
              <Select
                value={selectedPrinter}
                onChange={(e) => setSelectedPrinter(e.target.value)}
                displayEmpty
                disableUnderline
                IconComponent={ExpandMoreIcon}
                renderValue={(value) => {
                  if (!value) return <span style={{ color: "#aaa" }}>Select Printer</span>;
                  if (value === "default") {
                    return (
                      <span className={styles.mainTitle}>
                        Default {printerType === PRINTER_TYPES.DIRECT_USB ? "USB Without Driver" : printerType} Printer
                      </span>
                    );
                  }
                  const found = printers.find((p) => p.id === value || p.path === value || p.name === value);
                  if (found) return <span className={styles.mainTitle}>{found.name}</span>;

                  if (value.startsWith("\\\\?\\") || value.includes("#") || value.includes("&")) {
                    const match = value.match(/vid_([0-9a-fA-F]+)&pid_([0-9a-fA-F]+)/i);
                    if (match) {
                      const vid = match[1].toUpperCase();
                      const pid = match[2].toUpperCase();
                      const parts = value.split("#");
                      let sn = "";
                      if (parts.length > 2 && !parts[2].includes("&")) {
                        sn = parts[2];
                      }
                      return (
                        <span className={styles.mainTitle}>
                          USB Printer (VID: {vid}, PID: {pid}
                          {sn ? `, SN: ${sn}` : ""})
                        </span>
                      );
                    }
                    return <span className={styles.mainTitle}>USB Printer Device</span>;
                  }
                  return <span className={styles.mainTitle}>{value}</span>;
                }}
                sx={{
                  "& .MuiSelect-select": { padding: 0 },
                  "& .MuiSelect-icon": { color: "#7a7a7a", right: "8px" },
                }}
              >
                <MenuItem value="default">
                  Default {printerType === PRINTER_TYPES.DIRECT_USB ? "USB Without Driver" : printerType} Printer
                </MenuItem>
                {printers.map((printer) => (
                  <MenuItem
                    key={printer.id}
                    value={printerType === PRINTER_TYPES.DIRECT_USB ? printer.path : printer.name}
                  >
                    {printer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button className={styles.buttonRed} onClick={handleReloadPrinters}>
              Reload
            </Button>
          </Grid>
        </Grid>
      )}

      {printerType === PRINTER_TYPES.LAN && (
        <Grid item xs={12} className={styles.titleWrapper}>
          <Grid className={styles.formSection}>
            <PrinterIcon />
            <h3 className={styles.subTitle}>IP Address :</h3>
            <TextField
              variant="standard"
              placeholder="Enter Printer IP Address"
              value={ipAddress}
              onChange={(e) => setIpAddress(e.target.value)}
              className={styles.textField}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
        </Grid>
      )}

      {/* Invoice Format */}
      <Grid item xs={12} className={styles.titleWrapper}>
        <Grid className={styles.formSection}>
          <FormatIcon />
          <h3 className={styles.subTitle}>Invoice Format :</h3>
          <FormControl variant="standard" className={styles.selectWrapper}>
            <Select
              value={invoiceFormat}
              onChange={(e) => setInvoiceFormat(e.target.value)}
              displayEmpty
              disableUnderline
              IconComponent={ExpandMoreIcon}
              renderValue={(value) => {
                if (!value) return <span style={{ color: "#aaa" }}>Select Invoice Format</span>;
                return <span className={styles.mainTitle}>{value}</span>;
              }}
              sx={{
                "& .MuiSelect-select": { padding: 0 },
                "& .MuiSelect-icon": { color: "#7a7a7a", right: "8px" },
              }}
            >
              {INVOICE_PRINTER.invoiceFormat.map((format, i) => (
                <MenuItem key={i} value={format}>
                  {format}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Buttons */}
      <Grid item xs={12} display="flex" justifyContent="flex-end">
        <Button className={styles.buttonGray} onClick={handleTestPrint}>
          Test Print
        </Button>
        <Button className={styles.buttonRed} onClick={handleSave}>
          Save
        </Button>
      </Grid>
    </Grid>
  );
}

export default InvoicePrintersPage;
